<template>
  <div>
    <div
      class="mb-8 flex flex-col md:flex-row justify-between md:items-center -m-10 p-8 px-10 border-b shadow-sm"
    >
      <div class="flex flex-col">
        <div class="flex items-center leading-none">
          <span class="mr-2 text-xl">#{{ order.id }}</span>
          <span>
            <order-status-badge
              v-if="!loading"
              class="px-2 text-sm uppercase"
              :status="order.status"
            ></order-status-badge>
          </span>
        </div>
        <div class="mt-1 flex flex-col sm:mt-0 sm:flex-row sm:flex-wrap">
          <div class="mt-2 flex items-center text-sm leading-5 text-gray-600 sm:mr-6">
            <font-awesome-icon class="text-gray-500 mr-2 flex-shrink-0" icon="calendar" />
            {{ order.created_at.local | longDate }} Uhr
          </div>
        </div>
      </div>
      <div class="mt-5 flex lg:mt-0 lg:ml-4">
        <span class="ml-3 shadow-sm rounded-md" v-if="order.status == 'completed'">
          <a
            :href="pdfUrl"
            target="_blank"
            class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:text-gray-500 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 active:text-gray-800 active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out"
          >
            <font-awesome-icon class="-ml-1 mr-2 h-5 w-5" icon="print" />Drucken
          </a>
        </span>

        <span
          class="ml-3 shadow-sm rounded-md"
          v-if="order.status == 'completed' && currentAccount.linked_organizer_slug"
        >
          <a
            :href="`https://next.central.localticketing.de/${currentAccount.linked_organizer_slug}/orders/print/${order.id}`"
            target="_blank"
            class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:text-gray-500 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 active:text-gray-800 active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out"
          >
            <font-awesome-icon class="-ml-1 mr-2 h-5 w-5" icon="ticket-alt" />Drucken (BOCA)
          </a>
        </span>
        <span
          class="ml-3 shadow-sm rounded-md"
          v-if="order.status == 'completed' && currentAccount.linked_organizer_id"
        >
          <a
            :href="pdfOnlineTicketUrl"
            target="_blank"
            class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:text-gray-500 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 active:text-gray-800 active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out"
          >
            <font-awesome-icon class="-ml-1 mr-2 h-5 w-5" icon="ticket-alt" />E-Tickets drucken
          </a>
        </span>
        <span
          class="ml-3 shadow-sm rounded-md"
          v-if="
            order.status == 'completed' &&
            currentAccount.linked_organizer_id &&
            order.email !== null
          "
        >
          <button
            @click="sendTicketEmail"
            class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:text-gray-500 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 active:text-gray-800 active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out"
          >
            <font-awesome-icon class="-ml-1 mr-2 h-5 w-5" icon="envelope" />E-Mail verschicken
          </button>
        </span>
        <span class="ml-3 shadow-sm rounded-md" v-if="order.status == 'completed'">
          <a
            :href="`${order.id}/receipt`"
            target="_blank"
            class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:text-gray-500 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 active:text-gray-800 active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out"
          >
            <font-awesome-icon class="-ml-1 mr-2 h-5 w-5" icon="file-invoice" />Quittung
          </a>
        </span>
        <span class="sm:ml-3 shadow-sm rounded-md">
          <button
            @click="refundModal = true"
            :disabled="!refundable"
            type="button"
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm leading-5 font-medium rounded-md text-white focus:outline-none focus:shadow-outline-red focus:border-red-700 active:bg-red-700 transition duration-150 ease-in-out"
            :class="refundable ? 'hover:bg-red-400 bg-red-500' : 'bg-red-300 cursor-not-allowed'"
          >
            <font-awesome-icon class="-ml-1 mr-2 h-5 w-5" icon="times" />Zurückerstatten
          </button>
        </span>
      </div>
    </div>

    <div v-if="!loading">
      <div class="mt-10 flex w-full">
        <div class="p-6 w-2/3 shadow mr-4">
          <div class="text-xl">{{ count }} Tickets</div>

          <div class="pb-2 mt-8 mb-4 flex font-semibold justify-between border-b">
            <div class="w-1/6">#</div>

            <div class="w-2/6">Kategorie</div>

            <div class="w-2/6">Typ</div>

            <div class="w-1/6 text-right">Preis</div>
          </div>

          <div
            v-for="ticket in order.tickets"
            :key="ticket.id"
            class="py-4 flex justify-between items-center hover:bg-gray-100"
            :class="{ 'line-through': ticket.refunded }"
          >
            <div class="w-1/6">{{ ticket.code }}</div>

            <div class="w-2/6">{{ ticket.type.category.title }}</div>

            <div class="w-2/6">{{ ticket.type.title }}</div>

            <div class="w-1/6 text-right">{{ ticket.price | price }}</div>
          </div>

          <div class="text-right mt-4 font-semibold">
            Total für {{ count }} Tickets: {{ total | price }}
          </div>
        </div>

        <div class="w-1/3 ml-4">
          <div class="p-6 shadow">
            <div class="text-xl mb-6">Veranstaltung</div>

            <div class="w-32 object-cover">
              <img class="rounded" :src="order.event.media.logo.url" />
            </div>

            <div class="text-xl mt-4">{{ order.event.title }}</div>

            <div class="text-gray-800 mt-2">{{ order.event.start_date | shortDate }} Uhr</div>

            <div class="text-gray-800">
              {{ order.event.venue.name }}, {{ order.event.venue.city }}
            </div>
          </div>
        </div>
      </div>

      <order-refund-modal
        :order="order"
        v-if="refundModal"
        @close="refundModal = false"
        @refundSuccesfull="refundSuccesfull"
      />
    </div>
  </div>
</template>
<script>
import orderClient from '@/api/merchant/orders';
import OrderStatusBadge from '@/components/OrderStatusBadge';
import OrderRefundModal from '@/components/OrderRefundModal';
import _sumBy from 'lodash/sumBy';
import _filter from 'lodash/filter';
import dayjs from 'dayjs';

export default {
  components: { OrderStatusBadge, OrderRefundModal },
  data() {
    return {
      order: {},
      loading: true,
      refundModal: false,
    };
  },

  filters: {
    shortDate(value) {
      return dayjs(value).format('DD MMM YYYY HH:mm');
    },
    longDate(value) {
      return dayjs(value).format('dddd, DD MMMM YYYY HH:mm');
    },
    price(value) {
      var formatter = new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
      });

      return formatter.format(value / 100);
    },
  },

  computed: {
    total() {
      return _sumBy(this.order.tickets, (ticket) => ticket.price);
    },
    count() {
      return this.order.tickets.length;
    },
    pdfUrl() {
      return (
        process.env.VUE_APP_API_URL +
        `/merchant/orders/${this.order.id}/pdf?signature=${this.order.signature}`
      );
    },
    pdfOnlineTicketUrl() {
      return (
        process.env.VUE_APP_API_URL +
        `/commerce/orders/${this.order.id}/pdf?signature=${this.order.signature}`
      );
    },
    refundable() {
      return _filter(this.order.tickets, { refundable: true, refunded: false }).length > 0;
    },
    currentAccount() {
      return this.$store.state.account.currentAccount;
    },
  },

  mounted() {
    this.loadOrder();
  },

  methods: {
    refundSuccesfull() {
      this.refundModal = false;
      this.loadOrder();
    },
    loadOrder() {
      orderClient.show(this.$route.params.id).then(({ data }) => {
        this.order = data.data;
        this.loading = false;
      });
    },
    sendTicketEmail() {
      orderClient
        .sendEmail(this.$route.params.id)
        .then(({ data }) => {
          this.$toasted.show('Tickets wurden verschickt.', {
            position: 'bottom-right',
            theme: 'lt',
            type: 'success',
            duration: 5000,
          });
        })
        .catch(() => {
          this.$toasted.show('Tickets konnten nicht verschickt werden.', {
            type: 'error',
            theme: 'lt',
            duration: 3000,
            position: 'bottom-left',
          });
        });
    },
  },
};
</script>
